import React, { useState, useEffect, useRef } from 'react';
import { calculateOSTA } from '../ostaCalculator/ostaCalculator';
import { determineRisk, hasClinicalRisk } from '../riskCalculator/riskCalculator';
import { safeParseFloat } from '../utils/errorHandler';

// 結果画面。
// リスク判定の仕様は SPEC.md 「4. リスク判定ロジック」を参照。
//
// risk が確定できた場合は信号ランプとメッセージを表示し、Supabase へ送信する。
// 確定できなかった場合は信号ランプを一切描画せず、エラー文言のみを表示する
// （消灯したランプを出すと「異常なし」と誤読されるため）。SPEC.md 4.7(c)
const Results = ({ formData, setStep, setFormData, onRiskUpdate, hospitalInfo }) => {
    const [risk, setRisk] = useState(null);
    const [riskCalculated, setRiskCalculated] = useState(false);
    // 判定と送信を1セッションにつき1回だけ行うためのガード。
    // state だと React 18 の StrictMode による effect の二重実行時に
    // 更新が反映される前に2回目が走り、二重送信になりうるため ref を使う。
    const hasProcessedRef = useRef(false);

    useEffect(() => {
        sessionStorage.setItem('lastPage', 'Results');
        sessionStorage.setItem('has_navigated_to_results', 'true');
    }, []);

    useEffect(() => {
        if (hasProcessedRef.current) {
            return;
        }
        hasProcessedRef.current = true;

        // OSTA = (体重 - 年齢) * 0.2
        // 入力が欠けている場合は既定値で埋めず、判定不能として扱う。
        const weightValue = safeParseFloat(formData.weight, NaN);
        const ageValue = safeParseFloat(formData.age, NaN);
        const osta = calculateOSTA(weightValue, ageValue);

        const clinicalRisk = hasClinicalRisk(formData);

        const calculatedRisk = determineRisk({
            age: ageValue,
            gender: formData.gender,
            osta,
            previousFracture: formData.previousFracture === true,
            clinicalRisk
        });

        console.log('リスク判定:', {
            age: ageValue,
            gender: formData.gender,
            weight: weightValue,
            osta,
            previousFracture: formData.previousFracture === true,
            clinicalRisk,
            result: calculatedRisk
        });

        setRisk(calculatedRisk);
        setRiskCalculated(true);

        if (calculatedRisk === null) {
            // 判定できなかった場合は Supabase へ送信しない。
            // 捏造した risk を記録しないため。SPEC.md 4.8 原則5
            console.error('リスクを判定できませんでした。データ送信を行いません。', formData);
            return;
        }

        // 送信は画面描画をブロックしない（await しない）。SPEC.md 4.8 原則2
        try {
            onRiskUpdate(calculatedRisk, osta);
            sessionStorage.setItem('session_completed', 'true');
        } catch (error) {
            // 送信側で localStorage への退避と再試行が行われるため、ここでは表示を継続する
            console.error('リスク更新の呼び出しでエラーが発生しました:', error);
        }
    }, [formData, onRiskUpdate]);

    const getRiskMessage = () => {
        const highlightedText = (text, color) => (
            <span style={{ backgroundColor: color, color: 'black', padding: '0 4px', fontWeight: '900', fontSize: 'calc(1.5rem * var(--dynamic-font-size, 1))' }}>{text}</span>
        );

        switch (risk) {
            case 'blue':
                return (
                    <>
                        <div className="font-bold text-lg mb-2">{highlightedText('あなたは 青信号(異常なし) です。', 'skyblue')}</div>
                        <p className="font-bold text-lg mb-2">骨粗鬆症のリスクは低いですが、日々の栄養・運動に気をつけましょう。</p>
                    </>
                );
            case 'yellow':
                return (
                    <>
                        <div className="font-bold text-lg mb-2">{highlightedText('あなたは 黄信号(要指導) です。', 'yellow')}</div>
                        <p className="font-bold text-lg mb-2">栄養指導・運動指導を行います。医療機関での骨密度測定をご希望の方はお申し出ください。</p>
                    </>
                );
            case 'red':
                return (
                    <>
                        <div className="font-bold text-lg mb-2">{highlightedText('あなたは 赤信号(要精査①) です。', 'pink')}</div>
                        <p className="font-bold text-lg mb-2">骨粗しょう症のリスクが高いので、医療機関での骨密度測定を推奨します。</p>
                    </>
                );
            default:
                return null;
        }
    };

    // 本アプリの稼働ホストは www.honenavi.app のみ（院内利用）。
    // 自宅利用向けの文言を復活させる場合はここでホスト判定を追加する。
    const getMessageForHost = () => {
        return (
            <p className="font-bold text-lg text-red-600 mb-2">
                <span className="underline-wavy">この画面を開いたまま</span>、危険度チェックが完了した旨をスタッフにお声がけください。<br />
            </p>
        );
    };

    const getDentalMessage = () => {
        if (!formData.dentalCheck || formData.dentalIssue) {
            return (
                <p className="font-bold text-lg text-red-600 mb-2">
                    また、<span className="underline-wavy">歯科検査</span>を受けて、お口の中の状態を確認しておきましょう。
                </p>
            );
        }
        return null;
    };

    // 「最初の画面へ戻る」ボタンの処理。判定できた場合もできなかった場合も共通。
    const handleBackToStart = () => {
        sessionStorage.removeItem('has_navigated_to_results');
        sessionStorage.removeItem('session_id');

        // targetting=false の医療機関は事前質問（step 0,1）を飛ばして Step1 から始める
        const initialStep = hospitalInfo && hospitalInfo.targetting === false ? 2 : 0;
        setStep(initialStep);
        setFormData({
            weight: null,
            age: null,
            gender: null,
            height: null,
            previousFracture: null,
            parentHipFracture: null,
            currentSmoking: null,
            glucocorticoids: null,
            rheumatoidArthritis: null,
            secondaryOsteoporosis: null,
            alcohol: null,
            dentalCheck: null,
            dentalIssue: null,
            osteoporosisTreatment: null,
            boneDensityTest: null,
            isSubmitted: false
        });
        setRisk(null);
        setRiskCalculated(false);
        hasProcessedRef.current = false;
        console.log('セッションを終了し、新しいセッションの準備ができました');
    };

    const buttons = (
        <div className="flex items-center justify-between mt-4">
            <button
                onClick={handleBackToStart}
                className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
            >
                最初の画面へ戻る
            </button>

            <button
                onClick={() => setStep(13)}
                className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
            >
                検査の説明を見る(1分)
            </button>
        </div>
    );

    // 判定できなかった場合。信号ランプは描画しない。SPEC.md 4.7(c)
    if (riskCalculated && risk === null) {
        return (
            <div className="p-4 bg-red-100 rounded-lg">
                <h2 className="text-xl font-bold mb-4 dynamic-font">結果</h2>
                <div className="flex justify-center">
                    <div className="flex flex-col items-center text-center space-y-2">
                        <p className="font-bold text-lg text-red-600 mb-2">
                            何かエラーが起きてしまったようです。<br />
                            お手数ですが再度QRコードを読み取って、最初からご回答をお願いします。
                        </p>
                    </div>
                </div>
                <br />
                {buttons}
            </div>
        );
    }

    // 判定前の一瞬。ここで信号の枠だけ描画しないようにする。
    if (!riskCalculated) {
        return null;
    }

    return (
        <div className="p-4 bg-red-100 rounded-lg">
            <h2 className="text-xl font-bold mb-4 dynamic-font">結果</h2>
            <div className="flex justify-center mb-4">
                <div className="relative bg-gray-200 p-2 rounded-lg flex space-x-2 items-center justify-center">
                    <div className={`circle ${risk === 'blue' ? 'bg-blue-500' : 'bg-gray-300'} shadow`}>
                        <div className="light"></div>
                    </div>
                    <div className={`circle ${risk === 'yellow' ? 'bg-yellow-500' : 'bg-gray-300'} shadow`}>
                        <div className="light"></div>
                    </div>
                    <div className={`circle ${risk === 'red' ? 'bg-red-500' : 'bg-gray-300'} shadow`}>
                        <div className="light"></div>
                    </div>
                </div>
            </div>
            <div className="flex justify-center">
                <div className="flex flex-col items-center text-center space-y-2">
                    {getRiskMessage()}
                    {getDentalMessage()}
                </div>
            </div>
            <br />
            <div className="flex justify-center">
                {getMessageForHost()}
            </div>
            <br />
            {buttons}
        </div>
    );
};

export default Results;
